import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { $messageCentreStore } from '@lib/store';
import { cn, PaletteUtils } from '@shared/utils';
import { BaseInput, Button, FileInput, Text, Textarea } from '@shared/widgets';

interface IForm {
  subject: string;
  message: string;
  file: File | undefined;
}
const DEFAULT_VALUES: IForm = { subject: '', message: '', file: undefined };

interface NewMessageProps {
  className?: string;
}

export const NewMessage = (props: NewMessageProps) => {
  const { control, formState, handleSubmit, setValue, reset } = useForm<IForm>({ defaultValues: DEFAULT_VALUES });
  const { t } = useTranslation('messageCenter');

  const onSubmit = async (data: IForm) => {
    const formData = new FormData();
    data.file && formData.append('file', data.file);
    await $messageCentreStore.events.createNewThreadWithMessage({
      subject: data.subject,
      body: data.message,
      file: data.file ? formData : undefined,
    });
    reset(DEFAULT_VALUES);
  };

  return (
    <aside className={cn('w-80 bg-white p-8 dark:bg-soft-black', PaletteUtils.isVcfcPalette && 'max-md:mx-auto max-md:w-full', props.className)}>
      <Text weight='medium' size='lg' className='mb-4 text-center'>
        {t('newMessage.title')}
      </Text>
      <form className='flex flex-col gap-6' noValidate onSubmit={handleSubmit(onSubmit)}>
        <Controller
          name='subject'
          control={control}
          rules={{ required: t('errors.form.required', { ns: 'common' }) }}
          render={({ field, fieldState: { error } }) => (
            <BaseInput {...field} placeholder={t('newMessage.placeholders.enterSubject')} required error={error?.message} />
          )}
        />
        <Controller
          name='message'
          control={control}
          rules={{ required: t('errors.form.required', { ns: 'common' }) }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Textarea
              textareaClassName='m-0'
              fitContent
              placeholder={t('newMessage.placeholders.enterMessage')}
              required
              error={error?.message}
              value={value}
              onChange={onChange}
            />
          )}
        />
        <Controller
          name='file'
          control={control}
          render={({ field: { value, onChange } }) => <FileInput value={value} onChange={onChange} onRemove={() => setValue('file', undefined)} />}
        />
        <Button color='primary' fullWidth type='submit' disabled={formState.isSubmitting}>
          {t('buttons.send', { ns: 'common' })}
        </Button>
        <Text className='text-center' weight='light' size='sm' color='gray'>
          {t('newMessage.hint')}
        </Text>
      </form>
    </aside>
  );
};
